import React, {useEffect, useState} from 'react';
import {
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField
} from '@mui/material';
import {Badge, CurrencyExchange, Delete, DeleteSweep, Edit, History, Info, Person, Search} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';

import UserInfoModal from './modals/UserInfoModal';
import UserEditModal from './modals/UserEditModal';
import UserDeleteModal from './modals/UserDeleteModal';
import UserRemoveDeletionModal from './modals/UserRemoveDeletionModal';
import UserKycEditModal from './modals/UserKycEditModal';

import api from '../../../api/axios';
import Lottie from 'lottie-react';
import loadingAnimation from '../../../assets/lottie/loading.json';
import type {User} from '../../../../types/user/user';

const UserList = () => {
  const {t} = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editedUser, setEditedUser] = useState<User | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [kycOpen, setKycOpen] = useState(false);
  const [removeDeletionOpen, setRemoveDeletionOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [sortBy, setSortBy] = useState('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'DELETED' | 'DISABLED'>('ALL');

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.post('/user/admin-fetch-all-user', {
        search: search || null,
        sortBy,
        sortDirection,
        page,
        size: rowsPerPage,
      });

      setUsers(response.data.content);
      setTotalCount(response.data.totalElements);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, sortBy, sortDirection, search]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = sortBy === property && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleOpenInfo = (user: User) => {
    setSelectedUser(user);
    setInfoOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditedUser(user);
    setEditOpen(true);
  };

  const handleOpenDelete = (user: User) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleOpenKyc = (user: User) => {
    setSelectedUser(user);
    setKycOpen(true);
  };

  const handleOpenRemoveDeletion = (user: User) => {
    setSelectedUser(user);
    setRemoveDeletionOpen(true);
  };

  const handleConfirmEdit = (updatedUser: User) => {
    setEditOpen(false);
    setUsers(prev => prev.map(u => (u.userId === updatedUser.userId ? updatedUser : u)));
  };

  const handleConfirmDelete = () => {
    if (selectedUser) {
      setUsers(prev => prev.map(u => (u.userId === selectedUser?.userId ? {...u, deleted: true} : u)));
    }
    setDeleteOpen(false);
  };

  const handleRemoveConfirmDeletion = () => {
    if (selectedUser) {
      setUsers(prev => prev.map(u => (u.userId === selectedUser?.userId ? {...u, deleted: false} : u)));
    }
    setRemoveDeletionOpen(false);
  };

  const getKycStatusStyle = (status?: string) => {
    switch (status) {
      case 'APPROVED': return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'PENDING':
      case 'REVIEW': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'DECLINED': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-neutral-500/10 text-neutral-500 border-neutral-500/20';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'SP_SYSTEM_ADMIN': return 'text-red-500';
      case 'SP_ADMIN': return 'text-blue-500';
      case 'SP_SUPPORT': return 'text-green-500';
      default: return 'text-neutral-500';
    }
  };

  return (
      <div className="flex flex-col md:p-4 p-1 bg-white dark:bg-neutral-900/40 border border-gray-100 dark:border-white/5 h-[88vh]">
        {/* Header */}
        <div className="flex flex-col xl:flex-row justify-between items-center gap-4 mb-4 shrink-0">
          <div className="flex items-center gap-2 self-start xl:self-center">
            <Person className="text-dark-primary"/>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white uppercase tracking-tighter">
              {t('system.user.user')} <span className="text-neutral-500 font-light ml-2">Admin Control</span>
            </h2>
          </div>

          <div className="flex flex-wrap flex-row w-full md:max-w-5xl gap-3 justify-between md:justify-end items-center">
            <div className="flex bg-gray-50 dark:bg-neutral-800/50 p-1 rounded-none border border-black/5 dark:border-white/5 w-full md:w-auto order-3 md:order-1">
              {['ALL', 'ACTIVE', 'DISABLED', 'DELETED'].map((s) => (
                  <button
                      key={s}
                      onClick={() => { setStatusFilter(s as any); setPage(0); }}
                      className={`flex-1 md:flex-none px-4 py-1.5 text-xs font-bold uppercase tracking-tighter transition-all ${
                          statusFilter === s ? 'bg-dark-primary text-white shadow-lg' : 'text-neutral-500 hover:text-neutral-300'
                      }`}
                  >
                    {s}
                  </button>
              ))}
            </div>

            <div className="flex flex-1 md:flex-none gap-2 items-center w-full md:w-auto order-1 md:order-2">
              <TextField
                  size="small"
                  placeholder={t('system.user.searchbar')}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="bg-gray-50 dark:bg-neutral-800/50 flex-1 md:w-72"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {borderColor: 'rgba(0,0,0,0.05)', borderRadius: 0},
                    '& .MuiInputBase-input': {fontSize: '13px', color: 'white'}
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Search sx={{fontSize: 18, color: '#555'}}/></InputAdornment>
                  }}
              />
            </div>
          </div>
        </div>

        <Divider className="!border-gray-100 dark:!border-white/5"/>

        {loading && users.length === 0 ? (
            <div className="flex-1 flex justify-center items-center h-[400px]">
              <Lottie animationData={loadingAnimation} loop className="w-64" />
            </div>
        ) : (
            <div className="flex flex-col flex-1 overflow-hidden">
              <TableContainer
                  component={Paper}
                  className="bg-transparent shadow-none border-none mt-4 overflow-auto scrollbar-thin scrollbar-thumb-neutral-800"
                  sx={{borderRadius: 0, flex: 1, maxHeight: 'calc(88vh - 180px)'}}
              >
                <Table size="small" stickyHeader className="border-separate border-spacing-y-2">
                  <TableHead>
                    <TableRow className="bg-gray-50/50 dark:bg-neutral-900">
                      <TableCell className="!md:px-4 !px-1 !font-semibold text-gray-400 !uppercase !tracking-widest" sx={{border: 'none'}}>
                        <TableSortLabel
                            active={sortBy === 'id'}
                            direction={sortBy === 'id' ? sortDirection : 'asc'}
                            onClick={() => handleRequestSort('id')}
                            sx={{
                              '&.MuiTableSortLabel-active': { color: '#3b82f6' },
                              '& .MuiTableSortLabel-icon': { color: '#3b82f6 !important' }
                            }}
                        >
                          User Identity
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className="!md:px-4 !px-1 !font-semibold text-gray-400 !uppercase !tracking-widest" sx={{border: 'none', display: {xs: 'none', sm: 'table-cell'}}}>Contact & Address</TableCell>
                      <TableCell className="!md:px-4 !px-1 !font-semibold text-gray-400 !uppercase !tracking-widest" sx={{border: 'none', display: {xs: 'none', lg: 'table-cell'}}}>Steam Accounts</TableCell>
                      <TableCell className="!md:px-4 !px-1 !font-semibold text-gray-400 !uppercase !tracking-widest" sx={{border: 'none'}}>Security Status</TableCell>
                      <TableCell align="right" className="!md:px-4 !px-1 !font-semibold text-gray-400 !uppercase !tracking-widest" sx={{border: 'none'}}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(user => {
                      const isDeletedOrDisabled = user.deleted || !user.enabled;

                      return (
                          <TableRow
                              key={user.userId}
                              className={`group bg-gray-50/30 dark:bg-neutral-900/20 transition-all hover:bg-white/[0.04] ${isDeletedOrDisabled ? 'opacity-60' : ''}`}
                          >
                            <TableCell className="md:px-4 px-1" sx={{border: 'none'}}>
                              <div className="flex flex-col">
                                <span className="font-bold text-gray-100 text-sm leading-tight">{user.firstName} {user.lastName}</span>
                                <span className="text-[11px] text-neutral-500 font-mono italic">
                                  #{user.userId}
                                  <span className="hidden md:inline"> — {user.email}</span>
                                </span>
                                <span className={`text-[10px] font-bold uppercase mt-1 px-1 w-fit bg-black/20 ${getRoleColor(user.role)}`}>
                                  {user.role}
                                </span>
                              </div>
                            </TableCell>

                            <TableCell className="md:px-4 px-1" sx={{border: 'none', display: {xs: 'none', sm: 'table-cell'}}}>
                              <div className="flex flex-col text-xs text-neutral-400">
                                <span className="font-bold text-neutral-300">{user.street1}</span>
                                <span>{user.postalCode} {user.city}</span>
                                <span className="text-[10px] opacity-60">{user.phone}</span>
                              </div>
                            </TableCell>

                            <TableCell className="md:px-4 px-1" sx={{border: 'none', display: {xs: 'none', lg: 'table-cell'}}}>
                              <div className="max-h-16 overflow-y-auto pr-1">
                                {user.steamAccount?.length ? user.steamAccount.map(account => (
                                    <div key={account.steamId} className="text-[11px] font-mono text-neutral-500 group-hover:text-neutral-300 transition-colors mb-0.5">
                                      {account.profileName} <span className="opacity-40 text-[9px]">({account.steamId})</span>
                                    </div>
                                )) : <span className="text-[10px] text-neutral-600">None</span>}
                              </div>
                            </TableCell>

                            <TableCell className="md:px-4 px-1" sx={{border: 'none'}}>
                              <div className="flex flex-col gap-1">
                                <span className={`text-[10px] px-1.5 py-0.5 font-bold uppercase border w-fit ${getKycStatusStyle(user.kycStatus?.status)}`}>
                                  KYC: {user.kycStatus?.status || 'NOT_STARTED'}
                                </span>
                                <div className="text-[10px] font-bold tracking-tight">
                                  <span className="text-neutral-500 uppercase">2FA:</span>{' '}
                                  <span className={user.mfaEnabled ? 'text-emerald-500' : 'text-red-500'}>
                                    {user.mfaEnabled ? 'ENABLED' : 'DISABLED'}
                                  </span>
                                </div>
                              </div>
                            </TableCell>

                            <TableCell align="right" className="md:px-4 px-1" sx={{border: 'none'}}>
                              <div className="flex justify-end">
                                <div className="flex flex-wrap gap-0.5 justify-end max-w-[100px] md:max-w-none">
                                  <IconButton size="small" onClick={() => { setSelectedUser(user); setInfoOpen(true); }} className="!p-0.5 md:!p-1">
                                    <Info sx={{fontSize: 18}} className="text-dark-primary"/>
                                  </IconButton>
                                  <IconButton size="small" onClick={() => { setEditedUser(user); setEditOpen(true); }} className="!p-0.5 md:!p-1">
                                    <Edit sx={{fontSize: 18}} className="text-yellow-500"/>
                                  </IconButton>
                                  <IconButton size="small" onClick={() => user.deleted ? handleOpenRemoveDeletion(user) : handleOpenDelete(user)} className="!p-0.5 md:!p-1">
                                    {user.deleted ? <DeleteSweep sx={{fontSize: 18}} className="text-emerald-500"/> : <Delete sx={{fontSize: 18}} className="text-red-500"/>}
                                  </IconButton>
                                  <IconButton size="small" title="Transactions" className="!p-0.5 md:!p-1">
                                    <CurrencyExchange sx={{fontSize: 18}} className="text-yellow-600"/>
                                  </IconButton>
                                  <IconButton size="small" title="History" className="!p-0.5 md:!p-1">
                                    <History sx={{fontSize: 18}} className="text-neutral-400"/>
                                  </IconButton>
                                  <IconButton size="small" onClick={() => handleOpenKyc(user)} className="!p-0.5 md:!p-1">
                                    <Badge sx={{fontSize: 18}} className="text-blue-500"/>
                                  </IconButton>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  className="shrink-0 border-t border-white/5"
                  sx={{
                    color: '#888',
                    '& .MuiTablePagination-select': { borderRadius: 0, backgroundColor: 'rgba(255,255,255,0.02)' }
                  }}
              />
            </div>
        )}

        {/* Modals */}
        <UserInfoModal open={infoOpen} user={selectedUser} onClose={() => setInfoOpen(false)}/>
        <UserEditModal open={editOpen} userId={editedUser} onClose={() => setEditOpen(false)} onSave={handleConfirmEdit} />
        <UserDeleteModal open={deleteOpen} userId={selectedUser?.userId} userEmail={selectedUser?.email} onClose={() => setDeleteOpen(false)} onDeleted={handleConfirmDelete} />
        <UserRemoveDeletionModal open={removeDeletionOpen} userId={selectedUser?.userId} userEmail={selectedUser?.email} onClose={() => setRemoveDeletionOpen(false)} onDeleted={handleRemoveConfirmDeletion} />
        <UserKycEditModal open={kycOpen} userId={selectedUser?.userId} onClose={() => setKycOpen(false)} onUpdateKycStatus={(newStatus) => {
          if (selectedUser) {
            setUsers(prev => prev.map(u => u.userId === selectedUser.userId ? { ...u, kycStatus: { ...u.kycStatus, status: newStatus } } : u));
          }
        }} />
      </div>
  );
};

export default UserList;